import { useMemo, useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Fab from '@mui/material/Fab'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Portal from '@mui/material/Portal'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useConfirm } from 'material-ui-confirm'
import moment from 'moment'

import CSVIO from 'components/CSVIO'
import ErrorDialog from 'components/ErrorDialog'
import { Flex, FlexContainer } from 'components/Flex'
import ListAction from 'components/ListAction'
import ListFilter from 'components/ListFilter'
import ListSort from 'components/ListSort'
import Panel from 'components/Panel'
import StockDialog from 'components/StockDialog'
import StockListItem, { Mode } from 'components/StockListItem'
import {
  doc,
  generateId,
  orderBy,
  serverTimestamp,
  updateDoc,
  useDocs,
  where,
  writeBatch,
} from 'lib/firestore'
import { isMobile } from 'utils'
import { NO_DEADLINE_DATE, Role, categories } from 'utils/constant'

export default function StockList({ account }) {
  const confirm = useConfirm()
  const [searchText, setSearchText] = useState('')
  const [data, setData] = useState(null)
  const [sort, setSort] = useState({ field: 'expire', order: 'asc' })
  const [message, setMessage] = useState(null)
  const [filter, setFilter] = useState({
    'location.id': account.role === Role.LOCATION_MEMBER.value ? account.locationId : '*',
    category: '*',
    deleted: false,
  })
  const [error, setError] = useState(null)
  const conditions = useMemo(() => {
    const _conditions = []
    Object.entries(filter).forEach(([field, value]) => {
      if (value !== '*') {
        if (['true', 'false'].includes(value)) {
          value = JSON.parse(value)
        }
        _conditions.push(where(field, '==', value))
      }
    })
    // role 絞り込み中に role ソートしない
    if (filter.role === '*' || sort.field !== 'role') {
      _conditions.push(orderBy(sort.field, sort.order))
    }
    _conditions.push(orderBy('createdAt'))
    return _conditions
  }, [filter, sort])
  const { items } = useDocs(`accounts/${account.id}/stocks`, ...conditions)
  const { items: _locations } = useDocs(
    `accounts/${account.id}/locations`,
    where('deleted', '==', false)
  )
  const locations = useMemo(() => {
    if (account.role === Role.LOCATION_MEMBER.value) {
      return _locations.filter(x => x.id === account.locationId)
    }
    return _locations
  }, [account, _locations])

  const stocks = useMemo(() => {
    if (!items) {
      return null
    }
    return items.filter(x => {
      if (searchText === '') return true
      const regex = new RegExp(searchText)
      return Boolean(x.name.match(regex))
    })
  }, [items, searchText])

  const handleChangeFilter = (field, value) => {
    setFilter(current => ({ ...current, [field]: value }))
  }

  const handleChangeSort = field => {
    setSort(current => ({
      field,
      order: current.field !== field || current.order === 'desc' ? 'asc' : 'desc',
    }))
  }

  const handleArchive = item => {
    confirm({
      title: '削除しますか？',
      description: (
        <>
          削除するとメール通知の対象外になります。
          <br />
          絞り込みのステータスを「すべて」にすることで、削除後も確認できます。
        </>
      ),
      cancellationText: 'キャンセル',
    })
      .then(() => updateDoc(`accounts/${account.id}/stocks/${item.id}`, { deleted: true }))
      .catch(error => console.log(error))
  }

  const saveStocks = async values => {
    const batch = writeBatch()
    values.forEach(value => {
      const docId = value.id || generateId(`accounts/${account.id}/stocks`)
      const docRef = doc(`accounts/${account.id}/stocks/${docId}`)
      value.account = { id: account.id, name: account.name }
      if (!value.id) {
        value.createdAt = serverTimestamp()
      }
      value.updatedAt = serverTimestamp()
      batch.set(docRef, value, { merge: true })
    })
    await batch.commit()
    setMessage(`${values.length} 件の在庫を保存しました`)
  }

  if (!items) {
    return (
      <FlexContainer style={{ alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </FlexContainer>
    )
  }

  return (
    <Flex>
      <ListAction>
        <ListFilter
          value={filter}
          fields={[
            {
              id: 'location.id',
              name: '拠点',
              options: locations
                .sort((a, b) => {
                  a.category = a.category || ''
                  b.category = b.category || ''
                  return a.category === b.category
                    ? a.name > b.name
                      ? 1
                      : -1
                    : a.category && b.category
                    ? a.category > b.category
                      ? 1
                      : -1
                    : b.category
                    ? 1
                    : -1
                })
                .map(x => ({ label: x.name, value: x.id })),
              disabled: account.role === Role.LOCATION_MEMBER.value,
            },
            { id: 'category', name: 'カテゴリ', options: categories },
            {
              id: 'deleted',
              name: 'ステータス',
              options: [
                { label: 'アクティブ', value: false },
                { label: '削除済', value: true },
              ],
            },
          ]}
          onChange={handleChangeFilter}
        />
        <TextField
          variant="outlined"
          size="small"
          onChange={e => setSearchText(e.target.value)}
          value={searchText}
          placeholder="商品名を入力"
          fullWidth={false}
          sx={{ minWidth: 200, ml: 2, height: 40 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchText !== '' && (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearchText('')}>
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {!isMobile && (
          <>
            <Box sx={{ width: 2 }} />
            <CSVIO
              onChange={saveStocks}
              onError={error => setError(error.message)}
              format={{
                id: { name: 'ID' },
                code: { name: 'JAN コード' },
                name: { name: '商品名（必須）', required: '商品名は必須項目です' },
                category: {
                  name: 'カテゴリー',
                  convert: v => (categories.includes(v) ? v : 'その他'),
                },
                standard: { name: '規格' },
                price: { name: '単価', convert: v => parseInt(v) || 0 },
                amount: { name: '数量', convert: v => parseInt(v) || 0 },
                purchase: { name: '購入先' },
                location: {
                  name: '拠点（必須）',
                  required: '拠点は登録されている拠点名で入力してください',
                  convert: v => {
                    const location = locations.find(x => x.name === v)
                    return location ? { id: location.id, name: location.name } : null
                  },
                },
                storage: { name: '保管場所' },
                expire: {
                  name: '消費期限',
                  convert: v => {
                    const date = new Date(v)
                    if (date instanceof Date && !isNaN(date)) {
                      return date
                    } else {
                      return new Date(NO_DEADLINE_DATE)
                    }
                  },
                },
                memo: { name: '備考欄' },
                deleted: {
                  name: '削除する（はい or いいえ）',
                  convert: v => v === 'はい',
                },
              }}
              data={stocks.map(x => ({
                ...x,
                location: x.location.name,
                expire:
                  moment(x.expire).format('YYYY-MM-DD') === NO_DEADLINE_DATE
                    ? ''
                    : moment(x.expire).format('YYYY-MM-DD'),
                deleted: x.deleted ? 'はい' : 'いいえ',
              }))}
            />
          </>
        )}
      </ListAction>
      <Panel>
        <ListSort
          fields={[{ id: 'expire', name: '期限' }]}
          value={sort}
          onChange={handleChangeSort}
        />
        {stocks.map(x => (
          <StockListItem
            key={x.id}
            data={x}
            mode={Mode.ACCOUNT_STOCKLIST}
            onClick={() => setData(x)}
            onClickSecondary={handleArchive}
          />
        ))}
        {stocks.length === 0 && (
          <Box sx={{ p: 2, pt: 3, textAlign: 'center' }}>
            <Typography style={{ fontSize: 14, fontWeight: 'bold' }} variant="body2">
              商品は登録されていません
            </Typography>
            <Button color="primary" onClick={() => setData({})}>
              商品を登録する
            </Button>
          </Box>
        )}
      </Panel>
      <Fab
        color="primary"
        style={{ position: 'fixed', right: 16, bottom: 16 }}
        onClick={() => setData({})}
      >
        <AddIcon />
      </Fab>
      {data && (
        <StockDialog
          account={account}
          locations={locations}
          data={data}
          onClose={() => setData(null)}
          onCreate={() => setMessage('在庫を登録しました')}
          onEdit={() => setMessage('在庫情報を更新しました')}
          onArchive={() => setMessage('在庫を削除しました')}
          onRecover={() => setMessage('在庫を復元しました')}
          onDelete={() => setMessage('在庫を完全に削除しました')}
        />
      )}
      <Portal>
        <Snackbar open={message !== null} autoHideDuration={3000} onClose={() => setMessage(null)}>
          <Alert onClose={() => setMessage(null)} severity="success">
            {message}
          </Alert>
        </Snackbar>
      </Portal>
      {Boolean(error) && <ErrorDialog error={error} onClose={() => setError(null)} />}
    </Flex>
  )
}
